import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LocalOfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import BlockOutlined from "@mui/icons-material/BlockOutlined";
import TimeOffApprovedDrawer from "./TimeOffApprovedDrawer.js";
import TimeOffRequestDrawer from "./TimeOffRequestDrawer.js";
import "react-toastify/dist/ReactToastify.css";
import { formatDate } from "../../utilities/helpers";
import { get_t_str } from "../../../../../helpers/helpers";

function TimeOffApprovedCalendarModal(props) {
  const weekStartDayIndex =
    Number.isInteger(props.businessWeekStartDayIndex) &&
    props.businessWeekStartDayIndex >= 0 &&
    props.businessWeekStartDayIndex <= 6
      ? props.businessWeekStartDayIndex
      : 1;
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const orderedWeekdays = [
    ...weekdays.slice(weekStartDayIndex),
    ...weekdays.slice(0, weekStartDayIndex),
  ];

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "98%",
    height: "98%",
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
  };

  const blackoutModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
  };


  const [currentMonth, setCurrentMonth] = React.useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = React.useState(
    new Date().getFullYear()
  );
  const [days, setDays] = React.useState([]);
  const [blockedDates, setBlockedDates] = React.useState(
    props.blackoutDates
      ? props.blackoutDates.map((blockedDate) => ({
          ...blockedDate,
          date: new Date(`${blockedDate.date}T00:00:00`), // Ensures no timezone shift
        }))
      : []
  );

  React.useEffect(() => {
    if (props.blackoutDates) {
      setBlockedDates(
        props.blackoutDates.map((blockedDate) => ({
          ...blockedDate,
          date: new Date(`${blockedDate.date}T00:00:00`), // Ensures no timezone shift
        }))
      );
    }
  }, [props.blackoutDates]);

  const [blackoutModalOpen, setBlackoutModalOpen] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState(null);

  // Time off request drawer state
  const [isDrawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedRequest, setSelectedRequest] = React.useState(null);

  const changeMonth = (direction) => {
    setDays([]); // Reset days to avoid glitches during transition
    setTimeout(() => {
      if (direction === "next") {
        if (currentMonth === 11) {
          setCurrentMonth(0);
          setCurrentYear(currentYear + 1);
        } else {
          setCurrentMonth(currentMonth + 1);
        }
      } else if (direction === "prev") {
        if (currentMonth === 0) {
          setCurrentMonth(11);
          setCurrentYear(currentYear - 1);
        } else {
          setCurrentMonth(currentMonth - 1);
        }
      }
    }, 0);
  };

  React.useEffect(() => {
    const firstDayOfMonth =
      (new Date(currentYear, currentMonth, 1).getDay() - weekStartDayIndex + 7) % 7;
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const prevMonthDays = new Date(currentYear, currentMonth, 0).getDate();
    const daysArray = [];

    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      daysArray.push({ date: prevMonthDays - i, isCurrentMonth: false });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      daysArray.push({ date: i, isCurrentMonth: true });
    }

    while (daysArray.length % 7 !== 0) {
      const nextMonthDay = daysArray.length - daysInMonth - firstDayOfMonth + 1;
      daysArray.push({ date: nextMonthDay, isCurrentMonth: false });
    }

    setDays(daysArray);
  }, [currentMonth, currentYear, weekStartDayIndex]);

  const today = new Date();
  const todayDate = today.getDate();
  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  // Check if a date is blocked
  const isDateBlocked = (date) => {
    return blockedDates?.some(
      (blockedDate) =>
        blockedDate?.date?.getFullYear() === date?.getFullYear() &&
        blockedDate?.date?.getMonth() === date?.getMonth() &&
        blockedDate?.date?.getDate() === date?.getDate()
    );
  };

  // Check if a date is in the past
  const isPastDate = (date) => {
    const today = new Date(todayYear, todayMonth, todayDate);
    return date < today;
  };

  // Check if a date is clickable
  const isDateClickable = (date, isCurrentMonth) => {
    if (!isCurrentMonth) return false;
    return !isPastDate(new Date(currentYear, currentMonth, date));
  };

  // Handle date click to open blackout modal
  const handleDateClick = (date, isCurrentMonth) => {
    if (!isDateClickable(date, isCurrentMonth)) return;

    const fullDate = new Date(currentYear, currentMonth, date);
    setSelectedDate(fullDate);
    setBlackoutModalOpen(true);
  };

  // Save blackout date
  const handleSaveBlackout = () => {
    const existingBlockedDateIndex = blockedDates?.findIndex(
      (blockedDate) =>
        blockedDate?.date?.getFullYear() === selectedDate?.getFullYear() &&
        blockedDate?.date?.getMonth() === selectedDate?.getMonth() &&
        blockedDate?.date?.getDate() === selectedDate?.getDate()
    );

    if (existingBlockedDateIndex >= 0) {
      // Don't update - existing blocked date stays the same
    } else {
      // Add new blocked date
      const newBlockedDates = [
        ...blockedDates,
        {
          date: selectedDate,
        },
      ];
      setBlockedDates(newBlockedDates);

      if (props.create_blackout_date) {
        props.create_blackout_date(formatDate(selectedDate), "");
      }
    }

    setBlackoutModalOpen(false);
  };

  // Remove blackout date
  const handleRemoveBlackout = () => {
    const newBlockedDates = blockedDates.filter(
      (blockedDate) =>
        !(
          blockedDate?.date?.getFullYear() === selectedDate?.getFullYear() &&
          blockedDate?.date?.getMonth() === selectedDate?.getMonth() &&
          blockedDate?.date?.getDate() === selectedDate?.getDate()
        )
    );

    setBlockedDates(newBlockedDates);

    // If callback provided, pass the updated blocked dates
    if (props.delete_blackout_date) {
      props.delete_blackout_date(formatDate(selectedDate));
    }

    setBlackoutModalOpen(false);
  };

  // Handle time off request click
  const handleTimeOffClick = (timeOff) => {
    setSelectedRequest(timeOff);
    setDrawerOpen(true);
  };

  console.log("TIME OFF APPROVED", props.timeOffFutureApproved)

  return (
    <>
      <Modal
        open={props.open}
        onClose={() => props.handleClose()}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <Box sx={style}>
          <div
            className="absolute right-2 top-2 text-gray-500 cursor-pointer"
            onClick={() => props.handleClose()}
          >
            <CloseIcon />
          </div>
          <div>
            <div className="w-full flex items-center justify-between">
              <h3 className="text-xl">Time Off Calendar</h3>
              <div className="w-96 flex gap-x-8">
                <div className="flex items-center gap-x-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  <p className="text-emerald-700">Approved</p>
                </div>
                <div className="flex items-center gap-x-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-300"></div>
                  <p className="text-yellow-600">Pending</p>
                </div>
                <div className="flex items-center gap-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <p className="text-red-700">Blocked</p>
                </div>
              </div>
            </div>
            <div className="w-full flex px-12 mt-6">
              <div className="w-full pb-8">
                <div className="w-full flex justify-center items-center">
                  <div className="flex items-center text-slate-900 w-[50%] margin-auto">
                    <button
                      type="button"
                      className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-slate-500 hover:text-slate-600"
                      onClick={() => changeMonth("prev")}
                    >
                      <span className="sr-only">Previous month</span>
                      <ChevronLeftIcon />
                    </button>
                    <div className="flex-auto font-semibold text-lg text-center">
                      {months[currentMonth]} {currentYear}
                    </div>
                    <button
                      type="button"
                      className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-slate-500 hover:text-slate-600"
                      onClick={() => changeMonth("next")}
                    >
                      <span className="sr-only">Next month</span>
                      <ChevronRightIcon />
                    </button>
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-7 text-xs leading-6 text-slate-500">
                  {orderedWeekdays.map(
                    (dayOfWeek) => (
                      <div key={dayOfWeek}>{dayOfWeek}</div>
                    )
                  )}
                </div>
                <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-slate-200 text-sm shadow ring-1 ring-slate-200 overflow-hidden">
                  {days.map((day, dayIdx) => {
                    const isToday =
                      day.date === todayDate &&
                      currentMonth === todayMonth &&
                      currentYear === todayYear &&
                      day.isCurrentMonth;
                    const currentDate = new Date(
                      currentYear,
                      currentMonth,
                      day.date
                    );
                    const isPastCurrentDate = day.isCurrentMonth && isPastDate(currentDate);
                    const isBlocked = day.isCurrentMonth && isDateBlocked(currentDate);
                    const isClickable = isDateClickable(day.date, day.isCurrentMonth);

                    return (
                      <div
                        key={dayIdx}
                        className={classNames(
                          "min-h-[100px] relative pb-2 group",
                          day.isCurrentMonth
                            ? isBlocked
                              ? "bg-red-50"
                              : isPastCurrentDate && !isToday
                              ? "bg-gray-100" // Past dates have a gray background
                              : "bg-white"
                            : "bg-slate-50",
                        )}
                      >
                        {day.isCurrentMonth && (
                          <div>
                            <div className="absolute left-0.5 top-0.5">
                              <div
                                className={classNames(
                                  "flex h-[28px] w-[28px] items-center justify-center",
                                  isBlocked ? "bg-red-500 text-white rounded-full" : "",
                                  isToday ? "bg-violet-700 text-white rounded-full" : "",
                                  isPastCurrentDate && !isToday && !isBlocked ? "text-gray-400" : "",
                                  day.isCurrentMonth && !isBlocked && !isToday && !isPastCurrentDate ? "text-slate-900" : "",
                                  !day.isCurrentMonth ? "text-slate-200" : ""
                                )}
                              >
                                {day.date}
                              </div>
                              {isBlocked && (
                                <div className="absolute top-0 left-[32px] right-0 h-7 bg-red-100 rounded-r"></div>
                              )}
                            </div>
                            {(isClickable || isBlocked) && (
                              <div className="absolute right-0.5 top-0.5">
                                <div
                                  className="text-[9px] px-1 py-0.5 text-slate-400 hover:text-violet-600 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5"
                                  onClick={() => handleDateClick(day.date, day.isCurrentMonth)}
                                  title={isBlocked ? "Unblock date" : "Block date"}
                                >
                                  <BlockOutlined style={{ fontSize: "10px" }} />
                                  <span className="font-medium">{isBlocked ? "Unblock Date" : "Block Date"}</span>
                                </div>
                              </div>
                            )}
                            <div className="mt-10 px-1">
                              {isBlocked && (
                                <div className="bg-red-100 text-red-700 mt-1 rounded px-2 py-1">
                                  <p className="text-xxs font-bold">
                                    Date Blocked
                                  </p>
                                  <p className="text-xxs">
                                    No time off allowed
                                  </p>
                                </div>
                              )}
                              {props.timeOffFutureApproved.map((timeOff) => {
                                const startDate = new Date(
                                  timeOff.date_lower_bound + "T00:00:00"
                                );
                                const endDate = timeOff.date_upper_bound
                                  ? new Date(
                                      timeOff.date_upper_bound + "T23:59:59"
                                    )
                                  : startDate;
                                const currentDate = new Date(
                                  currentYear,
                                  currentMonth,
                                  day.date
                                );

                                const isInRange = (date) => {
                                  const start = new Date(
                                    startDate.getFullYear(),
                                    startDate.getMonth(),
                                    startDate.getDate()
                                  );
                                  const end = new Date(
                                    endDate.getFullYear(),
                                    endDate.getMonth(),
                                    endDate.getDate()
                                  );
                                  const current = new Date(
                                    date.getFullYear(),
                                    date.getMonth(),
                                    date.getDate()
                                  );

                                  return current >= start && current <= end;
                                };

                                if (isInRange(currentDate)) {
                                  return (
                                    <div
                                      key={timeOff.uuid}
                                      className={`${
                                        isPastCurrentDate && !isToday
                                          ? timeOff.status === "PENDING"
                                            ? "hidden"
                                            : "bg-slate-200 text-slate-400"
                                          : timeOff.status === "PENDING"
                                          ? "bg-yellow-200 text-yellow-700 hover:bg-yellow-300"
                                          : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                      } mt-1 rounded px-2 py-1 cursor-pointer transition-colors`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTimeOffClick(timeOff);
                                      }}
                                    >
                                      <p className="text-xxs font-bold">
                                        {timeOff.store_team_member.first_name}{" "}
                                        {timeOff.store_team_member.last_name}
                                      </p>
                                      <p className="text-xxs">
                                        {timeOff.title}
                                      </p>
                                      {timeOff.time_off_type && (
                                        <div className="flex items-center gap-x-1">
                                          <LocalOfferOutlined style={{ fontSize: '10px' }} />
                                          <p className="text-xxs">
                                            {timeOff.time_off_type === 'UNPAID' ? 'Unpaid' :
                                             timeOff.time_off_type === 'PTO' ? 'PTO' :
                                             timeOff.time_off_type === 'SICK' ? 'Sick' : ''}
                                          </p>
                                        </div>
                                      )}
                                      {(timeOff.start_qhnum && timeOff.end_qhnum) &&
                                        <p className="text-xxs">
                                          {get_t_str(timeOff.start_qhnum)} - {get_t_str(timeOff.end_qhnum)}
                                        </p>
                                      }
                                    </div>
                                  );
                                }
                                return null;
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Blackout Date Modal */}
      <Modal
        open={blackoutModalOpen}
        onClose={() => setBlackoutModalOpen(false)}
        aria-labelledby="blackout-modal-title"
      >
        <Box sx={blackoutModalStyle}>
          <div className="flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold" id="blackout-modal-title">
                {isDateBlocked(selectedDate) ? "Manage Blocked Date" : "Block Date"}
              </h3>
              <div
                className="absolute right-2 top-2 text-gray-500 cursor-pointer"
                onClick={() => setBlackoutModalOpen(false)}
              >
                <CloseIcon />
              </div>
            </div>
            <p className="mb-4">
              {selectedDate
                ? `${
                    months[selectedDate.getMonth()]
                  } ${selectedDate.getDate()}, ${selectedDate.getFullYear()}`
                : ""}
            </p>
            <div className="flex justify-end gap-x-3 mt-4">
              <button
                type="button"
                className="inline-flex justify-center py-2 px-4 border border-violet-700 shadow-sm text-sm font-medium rounded-lg text-violet-700 bg-white hover:bg-slate-50"
                onClick={() => setBlackoutModalOpen(false)}
              >
                Cancel
              </button>
              {isDateBlocked(selectedDate) && (
                <button
                  type="button"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700"
                  onClick={handleRemoveBlackout}
                >
                  Remove Block
                </button>
              )}
              {!isDateBlocked(selectedDate) && (
                <button
                  type="button"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-violet-700 hover:bg-violet-800"
                  onClick={handleSaveBlackout}
                >
                  Block Date
                </button>
              )}
            </div>
          </div>
        </Box>
      </Modal>

      {/* Time Off Request Drawer - For Approved/Rejected requests */}
      {selectedRequest && selectedRequest.status !== "PENDING" && (
        <TimeOffApprovedDrawer
          isDrawerOpen={isDrawerOpen}
          setDrawerOpen={setDrawerOpen}
          sx={{ zIndex: 1400 }}
          requestInfo={{
            firstName: selectedRequest.store_team_member.first_name,
            lastName: selectedRequest.store_team_member.last_name,
            title: selectedRequest.title,
            time_off_type: selectedRequest.time_off_type,
            startDate: selectedRequest.date_lower_bound,
            endDate: selectedRequest.date_upper_bound,
            startTime: selectedRequest.start_qhnum,
            endTime: selectedRequest.end_qhnum,
            note: selectedRequest.note,
            managerNote: selectedRequest.acting_manager_note,
            dateSubmitted: selectedRequest.created_at,
            dateResponded: selectedRequest.acting_manager_response_at,
            actedBy: selectedRequest.acting_manager_name,
            status: selectedRequest.status
          }}
          storeTimezone={props.storeTimezone}
        />
      )}

      {/* Time Off Request Drawer - For Pending requests */}
      {selectedRequest && selectedRequest.status === "PENDING" && (
        <TimeOffRequestDrawer
          isDrawerOpen={isDrawerOpen}
          setDrawerOpen={setDrawerOpen}
          sx={{ zIndex: 1400 }}
          requestInfo={{
            uuid: selectedRequest.uuid,
            firstName: selectedRequest.store_team_member.first_name,
            lastName: selectedRequest.store_team_member.last_name,
            title: selectedRequest.title,
            time_off_type: selectedRequest.time_off_type,
            startDate: selectedRequest.date_lower_bound,
            endDate: selectedRequest.date_upper_bound,
            startTime: selectedRequest.start_qhnum,
            endTime: selectedRequest.end_qhnum,
            note: selectedRequest.note,
            dateSubmitted: selectedRequest.created_at,
          }}
          respond_to_time_off_request={props.respond_to_time_off_request}
          storeTimezone={props.storeTimezone}
        />
      )}
    </>
  );
}

export default TimeOffApprovedCalendarModal;
